/**
 * @fileoverview Uygulama Durum Yonetimi Hook'u
 *
 * Bu modul, Emlakjet widget'inin temel uygulama durumunu (state)
 * yoneten merkezi hook'u icerir. Tema, goruntulenme modu, widget state
 * ve bolgesel fiyat bilgisi burada birlestirilir.
 *
 * @module useAppState
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useWidgetState } from "../../use-widget-state";
import { useOpenAiGlobal } from "../../use-openai-global";
import { createDefaultWidgetState, normalizeWidgetState } from "../utils";
import { useDisplayMode } from "./useDisplayMode";

/**
 * @typedef {Object} WidgetState
 * @property {Object} modelContent - ChatGPT ile paylasilan veri
 * @property {Array} modelContent.searchHistory - Arama gecmisi
 * @property {Array<string>} modelContent.savedListingIds - Kaydedilen ilan ID'leri
 * @property {Array<string>} modelContent.savedProjectIds - Kaydedilen proje ID'leri
 * @property {Object} privateContent - Widget-only ozel veri
 * @property {string|null} privateContent.selectedListingId - Secili ilan ID'si
 */

/**
 * @typedef {Object} UseAppStateReturn
 * @property {string} theme - Mevcut tema ("light" | "dark")
 * @property {boolean} isDark - Karanlik mod etkin mi
 * @property {boolean} isFullscreen - Tam ekran modu etkin mi
 * @property {Function} requestFullscreen - Tam ekran moduna gec
 * @property {Function} exitFullscreen - Tam ekran modundan cik
 * @property {WidgetState} widgetState - Normalize edilmis widget state
 * @property {Function} updateWidgetState - Widget state guncelleme fonksiyonu
 * @property {Object|null} regionPriceInfo - Bolgesel fiyat bilgisi
 * @property {Function} setRegionPriceInfo - Bolgesel fiyat bilgisi set fonksiyonu
 * @property {number|null} regionAveragePrice - Bolge ortalama fiyati
 */

/**
 * Temel uygulama durum yonetimi hook'u.
 *
 * Bu hook, widget'in tum temel durumunu (state) tek bir yerde birlestirir.
 * Tema, goruntulenme modu, widget state ve bolgesel fiyat bilgisi
 * burada yonetilir.
 *
 * **Yonetilen State'ler:**
 * - **Tema:** ChatGPT'den gelen tema (light/dark), document'a uygulanir
 * - **Display Mode:** Fullscreen/inline modu, ChatGPT API ile yonetilir
 * - **Widget State:** Arama gecmisi, kaydedilen ilanlar, secili ilan
 * - **Region Price:** Bolgesel fiyat bilgisi (ortalama, amortizasyon)
 *
 * **State Normalizasyonu:**
 * Widget state her zaman `normalizeWidgetState` ile normalize edilir.
 * Bu, eksik alanlarin varsayilan degerlerle doldurulmasini saglar.
 *
 * **Tema Entegrasyonu:**
 * Tema degisikliklerinde `document.documentElement.setAttribute("data-theme", ...)`
 * ile Tailwind dark mode class'lari aktive edilir.
 *
 * @function useAppState
 * @returns {UseAppStateReturn} Birlestirilmis uygulama state'i ve fonksiyonlari
 *
 * @example
 * const {
 *   theme,
 *   isDark,
 *   isFullscreen,
 *   requestFullscreen,
 *   exitFullscreen,
 *   widgetState,
 *   updateWidgetState,
 *   regionPriceInfo,
 *   setRegionPriceInfo,
 *   regionAveragePrice,
 * } = useAppState();
 *
 * // Tema kullanimi
 * const bgColor = isDark ? "bg-gray-900" : "bg-white";
 *
 * // Widget state guncelleme
 * updateWidgetState((state) => ({
 *   ...state,
 *   privateContent: {
 *     ...state.privateContent,
 *     selectedListingId: "12345",
 *   },
 * }));
 */
export function useAppState() {
  // Theme
  const theme = useOpenAiGlobal("theme");
  const isDark = theme === "dark";

  // Apply theme to document for Tailwind dark: classes
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme || "light");
  }, [theme]);

  // Display mode (fullscreen/inline)
  const { isFullscreen, requestFullscreen, exitFullscreen } = useDisplayMode();

  // Widget state management
  const [widgetState, setWidgetState] = useWidgetState(createDefaultWidgetState);
  const normalizedWidgetState = useMemo(
    () => normalizeWidgetState(widgetState),
    [widgetState]
  );

  /**
   * Widget state guncelleme fonksiyonu.
   *
   * Normalizasyon sadece okuma sirasinda yapilir (normalizedWidgetState memoized).
   * Write path'te normalize etmiyoruz cunku:
   * 1. performans: Her write'ta gereksiz normalize isleminden kaciniyoruz
   * 2. prev zaten normalizedWidgetState'den geliyor varsayimi
   */
  const updateWidgetState = useCallback(
    (updater) => {
      setWidgetState((prev) => {
        // prev, useWidgetState'ten gelen raw state. Normalization read-side'da
        // yapiliyor, bu yuzden burada sadece updater'i calistiriyoruz.
        const normalized = normalizeWidgetState(prev);
        const next = typeof updater === "function" ? updater(normalized) : normalized;
        return next;
      });
    },
    [setWidgetState]
  );

  // Region price state
  const [regionPriceInfo, setRegionPriceInfo] = useState(null);
  const regionAveragePrice = useMemo(() => {
    const price = regionPriceInfo?.info?.currentLocationPrice;
    return typeof price === "number" ? price : null;
  }, [regionPriceInfo]);

  return {
    // Theme
    theme,
    isDark,
    // Display mode
    isFullscreen,
    requestFullscreen,
    exitFullscreen,
    // Widget state
    widgetState: normalizedWidgetState,
    updateWidgetState,
    // Region price
    regionPriceInfo,
    setRegionPriceInfo,
    regionAveragePrice,
  };
}

export default useAppState;
